import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

/**
 * 🔴 A LIST'S FILTERS LIVE IN THE URL — DS-10d (scope §3.2).
 *
 * A filtered list that forgets its filters on Back, on a reload or in a link sent to a colleague is a list the person
 * filters again every time. So the filters are search params: `useListSearch` reads them and writes them, and the
 * route's `validateSearch` (`listSearch()`, below) decides what a hand-edited URL may put there.
 *
 * - **App code, not the kit.** This imports TanStack Router at runtime; `src/components` may not (ui-gates.spec.ts), so
 *   a kit component is handed what this returns rather than calling the router itself.
 * - **`replace: true`.** Typing in a filter is not a navigation: every keystroke pushing an entry would turn Back into
 *   an undo of single letters. Back leaves the list; it does not replay the typing.
 * - **The cursor NEVER goes into the URL** (`after`, `cursor`): it carries its listing's fingerprint, so a deep-linked
 *   cursor goes stale the moment the listing moves. Paging is the page's state, and `useCursorList`'s.
 * - **An empty value clears its key**, so a cleared filter leaves `?status=` out of the URL rather than in it.
 */
export type ListSearch = Readonly<Partial<Record<string, string>>>;

/** A change to the URL's filters. A cursor is not a filter: the types refuse it, and so does the write. */
export type ListSearchPatch = Partial<Record<string, string>> & { after?: never; cursor?: never };

const CURSOR = ['after', 'cursor'];

/**
 * The router JSON-parses a search value, so `?done=true` arrives as a boolean and `?q=2024` as a number: both are read
 * back as the text that was typed. Anything else (an object, an array) is not a filter.
 */
const asText = (v: unknown): string | undefined =>
  typeof v === 'string' ? v || undefined : typeof v === 'number' || typeof v === 'boolean' ? String(v) : undefined;

/** The current location's list filters, as text, and a writer that replaces them. */
export function useListSearch(): [ListSearch, (patch: ListSearchPatch) => void] {
  const raw = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const search = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw)) {
      const s = asText(v);
      if (s !== undefined && !CURSOR.includes(k)) out[k] = s;
    }
    return out;
  }, [raw]);
  const setSearch = useCallback(
    (patch: ListSearchPatch) =>
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev, ...patch };
          for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === '' || CURSOR.includes(k)) delete next[k];
          return next;
        },
        replace: true,
      } as never),
    [navigate],
  );
  return [search, setSearch];
}

/**
 * 🔴 A DAY POSTGRES WILL TAKE, in ISO_DATE's shape. The shape alone is also all the server checks (`@Matches(ISO_DATE)`,
 * paged-query.dto.ts), so a hand-edited `2026-13-45` or `2026-02-30` passed both and came back from Postgres as 22008,
 * a 400 — an error state where the URL should read as no filter. So the day must be on the calendar (`Date.parse`
 * rolls 02-30 over to 03-02, so the round trip is compared) and after year 0, which Postgres does not have. `toJSON`
 * rather than `toISOString`: an invalid date answers `null` (read as the text "null") instead of throwing — and it is
 * shorter, which counts here (below).
 */
const isDay = (v: string): boolean =>
  /^\d{4}-\d\d-\d\d$/.test(v) && v >= '0001' && String(new Date(v).toJSON()).startsWith(v);

/** What a list route's `validateSearch` answers: text only, and only what passed. */
export type ListSearchParams<E extends string = never> = Partial<Record<'status' | 'from' | 'to' | 'q' | E, string>>;

/**
 * 🔴 THE LIST ROUTES' `validateSearch` (routes.tsx): `listSearch()`, or `listSearch('kind', 'done')` for a list with
 * extra dimensions. Text only — `status` and each named extra up to 40 characters, `from`/`to` a real day, `q` up to
 * 100 (longer is cut, not dropped, so a long paste still searches). A value that fails reads as `undefined`, which is
 * "no filter", never a 400. It judges only these keys; a list reads only these keys, and `useListSearch` never reads
 * nor writes a cursor.
 *
 * ⚠️ On the back office's first load (routes.tsx), so it is kept small: the ceiling is a few hundred bytes away.
 */
export function listSearch<E extends string = never>(...extras: E[]) {
  return (s: Record<string, unknown>): ListSearchParams<E> => {
    // ⚠️ EVERY key it judges is answered, a failure as `undefined`: the router lays a route's answer OVER the root's
    // unvalidated search, so a key left out would still read through `useSearch({ strict: false })` as typed.
    const out: Record<string, string | undefined> = {};
    for (const k of ['status', 'from', 'to', 'q', ...extras]) {
      const v = asText(s[k]);
      out[k] = k === 'q' ? v?.slice(0, 100) : v && (k === 'from' || k === 'to' ? isDay(v) : v.length <= 40) ? v : undefined;
    }
    return out as ListSearchParams<E>;
  };
}
